import React, { memo, useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { injectIntl, FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';
import withStyles from '@material-ui/core/styles/withStyles';
import Hidden from '@material-ui/core/Hidden';
import Button from '@material-ui/core/Button';

import isObject from 'lodash/isObject';
import isEmpty from 'lodash/isEmpty';
import messages from 'aurora-translations';

import filters from 'aurora-utils/filters.json';
import { fetchNationalities } from 'composition/modules/buyersList/actions';
import ADGMImage from '../../ADGMImage';
import SearchFilters from './SearchFilters';

import MobileFilters from './MobileFilters';
import styles from './styles';

const ActionBar = props => {
  const {
    classes,
    intl,
    locale,
    loading,
    hiddenFilters,
    query,
    handleQueryChange,
    initialFormState,
    formData,
    setFormData,
    nationalities,
  } = props;
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);

  const dispatch = useDispatch();

  useEffect(() => {
    const defaultNationalLabel = nationalities.filter(national => national.value === query?.nationalityId)[0]?.label;
    setFormData({
      passportNumber: query?.passportNumber || '',
      nationalityId: query?.nationalityId ? { value: query?.nationalityId, label: defaultNationalLabel } : {},
      email: query?.email || '',
      mobileNumber: query?.mobileNumber || '',
    });
  }, [query]);

  useEffect(() => {
    dispatch(fetchNationalities());
  }, []);

  const handleChange = useCallback(event => {
    const { name, value } = event.target;
    setFormData(data => ({ ...data, [name]: value }));
  }, []);

  const handleSelectChange = useCallback((name, value) => {
    setFormData(data => ({ ...data, [name]: value || undefined }));
  }, []);

  const handleCheckboxChange = useCallback(event => {
    const { name, checked } = event.target;
    setFormData(data => ({ ...data, [name]: checked }));
  }, []);

  const handleResetForm = useCallback(() => {
    const newQuery = {};

    setFormData(initialFormState);
    handleQueryChange(newQuery);
  }, [query.type]);

  const getFormValue = data => (isObject(data) ? data?.value : data);

  const handleSearch = useCallback(
    event => {
      event?.preventDefault();
      const newFilters = {
        ...(getFormValue(formData.passportNumber) && { passportNumber: getFormValue(formData.passportNumber) }),
        ...(getFormValue(formData.nationalityId) && { nationalityId: getFormValue(formData.nationalityId) }),
        ...(getFormValue(formData.email) && { email: getFormValue(formData.email) }),
        ...(!isEmpty(formData.mobileNumber?.trim()) && { mobileNumber: formData.mobileNumber.trim() }),
      };
      const newQuery = {
        page: 1,
        ...newFilters,
      };
      handleQueryChange(newQuery);
      // return;
    },
    [formData, query],
  );

  const toggleMobileFilterModal = useCallback(() => setIsMobileFilterOpen(prev => !prev), []);

  const getDropdownData = useCallback(
    key => {
      return filters[key]?.map(item => ({
        value: item.value,
        label: locale === 'ar' ? item.labelAr : item.labelEn,
      }));
    },
    [locale],
  );

  return (
    <form noValidate autoComplete='off' onSubmit={handleSearch}>
      <Hidden smDown>
        {/* Above form fields */}
        <SearchFilters
          intl={intl}
          loading={loading}
          dropdownData={nationalities}
          formData={formData}
          hiddenFilters={hiddenFilters}
          getDropdownData={getDropdownData}
          handleChange={handleChange}
          handleSelectChange={handleSelectChange}
          handleResetForm={handleResetForm}
          locale={locale}
        />
      </Hidden>

      <div className={classes.mobilefilterframe}>
        <Hidden mdUp>
          <Button onClick={toggleMobileFilterModal} className={classes.mobilefiltertext}>
            <ADGMImage name='mobileFilterIcon.svg' alt='filtericon' /> <FormattedMessage {...messages.Filters} />
          </Button>
        </Hidden>
      </div>

      {/* Mobile filters modal */}
      <Hidden mdUp>
        <MobileFilters
          intl={intl}
          isModalOpen={isMobileFilterOpen}
          formData={formData}
          hiddenFilters={hiddenFilters}
          getDropdownData={getDropdownData}
          handleChange={handleChange}
          handleSelectChange={handleSelectChange}
          handleCheckboxChange={handleCheckboxChange}
          handleResetForm={handleResetForm}
          handleSearch={handleSearch}
          toggleModal={toggleMobileFilterModal}
          dropdownData={nationalities}
          locale={locale}
        />
      </Hidden>
    </form>
  );
};

ActionBar.propTypes = {
  intl: PropTypes.object.isRequired,
  locale: PropTypes.object.isRequired,
  loading: PropTypes.bool,
  isOwnership: PropTypes.bool.isRequired,
  hiddenFilters: PropTypes.array.isRequired,
  query: PropTypes.object.isRequired,
  classes: PropTypes.object.isRequired,
  initialFormState: PropTypes.object.isRequired,
  formData: PropTypes.object.isRequired,
  setFormData: PropTypes.func.isRequired,
  handleQueryChange: PropTypes.func.isRequired,
  nationalities: PropTypes.array,
};

ActionBar.defaultProps = {
  loading: false,
  nationalities: [],
};

export default withStyles(styles)(injectIntl(memo(ActionBar)));
